import {
  collection,
  query,
  orderBy,
  limit,
  where,
  getDocs,
  Timestamp, // Import Timestamp for type checking or conversion if needed
} from "firebase/firestore";
import { db, auth } from "./firebase"; // Import auth as well
import { AppError, ErrorCodes, handleError } from "../utils/errorUtils";
import { formatISO } from "date-fns"; // For reliable YYYY-MM-DD formatting

const IMAGES_COLLECTION = "webcamImages";

// Direct Google Cloud Storage URL builder
const buildDirectGCSUrl = (fileName) => {
  return `https://storage.googleapis.com/webcam_modellflugplatz/${fileName}`;
};

// Helper function to convert Firestore Timestamps to JavaScript Dates
const convertTimestamps = (data) => {
  const converted = { ...data };

  // Convert common timestamp fields
  if (converted.timestamp instanceof Timestamp) {
    converted.timestamp = converted.timestamp.toDate();
  }
  if (converted.createdAt instanceof Timestamp) {
    converted.createdAt = converted.createdAt.toDate();
  }
  if (converted.storageUpdatedAt instanceof Timestamp) {
    converted.storageUpdatedAt = converted.storageUpdatedAt.toDate();
  }
  if (converted.scriptImportedAt instanceof Timestamp) {
    converted.scriptImportedAt = converted.scriptImportedAt.toDate();
  }

  return converted;
};

/**
 * Fetches the metadata for the latest image from Firestore
 * and then gets its download URL from Storage.
 */
export const getLatestImage = async () => {
  try {
    // Check authentication state
    const currentUser = auth.currentUser;
    console.log("🔐 Auth state check:", {
      currentUser: currentUser ? "Authenticated" : "Not authenticated",
      uid: currentUser?.uid || "N/A",
      email: currentUser?.email || "N/A",
    });

    console.log("🔍 Fetching latest image metadata from Firestore...");

    const imagesCollectionRef = collection(db, IMAGES_COLLECTION);
    const latestImageQuery = query(
      imagesCollectionRef,
      orderBy("storageUpdatedAt", "desc"),
      limit(1)
    );

    const querySnapshot = await getDocs(latestImageQuery);

    if (querySnapshot.empty) {
      throw new AppError(
        "No images found in the database",
        ErrorCodes.FIRESTORE_NODATA
      );
    }

    const latestImageDoc = querySnapshot.docs[0];
    const imageData = latestImageDoc.data();

    console.log("✅ Found latest image metadata:", {
      filename: imageData.filename,
      storageUpdatedAt: imageData.storageUpdatedAt,
      createdAt: imageData.createdAt,
      timestamp: imageData.timestamp,
      date: imageData.date,
    });

    if (!imageData.filePath) {
      throw new AppError(
        "Image metadata is incomplete - missing filePath",
        ErrorCodes.FIRESTORE_DATA_INCOMPLETE
      );
    }

    console.log("🔗 Getting download URL for:", imageData.filePath);
    console.log("🔐 Auth token available:", !!currentUser?.accessToken);

    // Use direct Google Cloud Storage URL instead of Firebase Storage
    const downloadURL = buildDirectGCSUrl(imageData.filePath);
    console.log("✅ Using direct GCS URL:", downloadURL);

    // Convert Firestore Timestamps to JavaScript Dates
    const convertedMetadata = convertTimestamps(imageData);

    return {
      url: downloadURL,
      metadata: convertedMetadata,
    };
  } catch (error) {
    console.error("❌ Error in getLatestImage:", error);
    throw handleError(error, "Failed to fetch latest image");
  }
};

/**
 * Fetches all images for a specific date from Firestore
 * and returns them with their download URLs.
 */
export const getImagesByDate = async (dateObject) => {
  try {
    // Check authentication state
    const currentUser = auth.currentUser;
    console.log("🔐 Auth state check for date images:", {
      currentUser: currentUser ? "Authenticated" : "Not authenticated",
      uid: currentUser?.uid || "N/A",
      email: currentUser?.email || "N/A",
    });

    if (!dateObject || !(dateObject instanceof Date)) {
      throw new AppError("Invalid date provided", ErrorCodes.INVALID_INPUT);
    }

    // Format date as YYYY-MM-DD for Firestore query
    const dateStr = formatISO(dateObject, { representation: "date" });
    console.log("🔍 Fetching images for date:", dateStr);

    const imagesCollectionRef = collection(db, IMAGES_COLLECTION);
    const dateQuery = query(
      imagesCollectionRef,
      where("date", "==", dateStr),
      orderBy("storageUpdatedAt", "desc")
    );

    const querySnapshot = await getDocs(dateQuery);

    if (querySnapshot.empty) {
      console.log("📭 No images found for date:", dateStr);
      return [];
    }

    console.log(`✅ Found ${querySnapshot.docs.length} images for ${dateStr}`);

    // Get download URLs for all images using direct GCS URLs
    const imagesWithUrls = await Promise.all(
      querySnapshot.docs.map(async (doc, index) => {
        const imageData = doc.data();
        console.log(
          `🔗 Getting URL for image ${index + 1}/${querySnapshot.docs.length}:`,
          imageData.filePath
        );

        try {
          // Use direct Google Cloud Storage URL
          const downloadURL = buildDirectGCSUrl(imageData.filePath);

          // Convert Firestore Timestamps to JavaScript Dates
          const convertedMetadata = convertTimestamps(imageData);

          return {
            id: doc.id,
            url: downloadURL,
            metadata: convertedMetadata,
          };
        } catch (error) {
          console.error(
            `❌ Error getting URL for ${imageData.filePath}:`,
            error
          );
          return {
            id: doc.id,
            url: null,
            metadata: convertTimestamps(imageData),
            error: error.message,
          };
        }
      })
    );

    console.log(`✅ Successfully processed ${imagesWithUrls.length} images`);
    return imagesWithUrls;
  } catch (error) {
    console.error("❌ Error in getImagesByDate:", error);
    throw handleError(error, `Failed to fetch images for date`);
  }
};

/**
 * Fetches all available dates that have images in the database
 * Returns an array of date strings in YYYY-MM-DD format
 */
export const getAvailableDates = async () => {
  try {
    // Check authentication state
    const currentUser = auth.currentUser;
    console.log("🔐 Auth state check for available dates:", {
      currentUser: currentUser ? "Authenticated" : "Not authenticated",
      uid: currentUser?.uid || "N/A",
      email: currentUser?.email || "N/A",
    });

    console.log("🔍 Fetching all available dates from Firestore...");

    const imagesCollectionRef = collection(db, IMAGES_COLLECTION);
    const allImagesQuery = query(imagesCollectionRef, orderBy("date", "desc"));

    const querySnapshot = await getDocs(allImagesQuery);

    if (querySnapshot.empty) {
      console.log("📭 No images found in database");
      return [];
    }

    // Extract unique dates
    const uniqueDates = new Set();
    querySnapshot.docs.forEach((doc) => {
      const imageData = doc.data();
      if (imageData.date) {
        uniqueDates.add(imageData.date);
      }
    });

    const availableDates = Array.from(uniqueDates).sort((a, b) =>
      b.localeCompare(a)
    ); // Sort descending
    console.log(
      `✅ Found ${availableDates.length} unique dates with images:`,
      availableDates
    );

    return availableDates;
  } catch (error) {
    console.error("❌ Error in getAvailableDates:", error);
    throw handleError(error, "Failed to fetch available dates");
  }
};
